import { useEffect, useState } from "react";
import { Chip, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";

import dayjs from "dayjs";

import {
  FaBed,
  FaBroom,
  FaClock,
  FaEdit,
  FaExclamationCircle,
  FaPlus,
  FaSave,
  FaSignInAlt,
  FaSignOutAlt,
  FaStickyNote,
  FaTimes,
  FaTrash,
  FaUser,
  FaUserPlus,
} from "react-icons/fa";

import VuiBox from "components/VuiBox";
import VuiButton from "components/VuiButton";
import VuiInput from "components/VuiInput";
import VuiTypography from "components/VuiTypography";
import GuestFlagChip from "components/GuestFlagChip";
import VuiTooltip from "components/VuiTooltip";

import colors from "assets/theme/base/colors";
import rgba from "assets/theme/functions/rgba";

import useCommon from "hooks/useCommon";
import usePermissions from "hooks/usePermissions";

import { resetDeleteNoteState, resetSaveNoteState } from "store/slice/housekeepingSlice";

import { getOccupancyStateConfig, getResourceStateConfig } from "../constants";

import { FLAG_CONFIG } from "variables";
import useHotelDateTime from "hooks/useHotelDateTime";

const { primary, warning, info, dark, light } = colors;

const getGuestName = (room) =>
  room?.guestName ||
  room?.guest?.name ||
  room?.guest?.fullName ||
  room?.reservation?.guestName ||
  room?.reservation?.guest?.name ||
  "";

const getGuestFlag = (room) =>
  room?.guestFlag || room?.guest?.flag || room?.reservation?.guestFlag || null;

const getFlagType = (flag) => {
  if (!flag) return "";

  return typeof flag === "string" ? flag : flag?.type || flag?.flagType || "";
};

const getFlagReason = (flag) => {
  if (!flag || typeof flag !== "object") {
    return "";
  }

  return flag?.reason || flag?.description || flag?.message || "";
};

const getRoomNote = (room) => {
  if (room?.note && typeof room.note === "object") {
    if (!room.note?.text && !room.note?.message) {
      return null;
    }

    return room.note;
  }

  if (Array.isArray(room?.notes) && room.notes.length > 0) {
    return room.notes[0];
  }

  return null;
};

const getNoteText = (note) => note?.text || note?.message || "";

function RoomDetailDialog({
  open,
  room,
  onClose,
  onAssign,
  onUpdateStatus,
  onAddNote,
  onUpdateNote,
  onDeleteNote,
  canAssign = true,
  canUpdateStatus = true,
  canAddNote = true,
}) {
  const { STORE, DISPATCH } = useCommon();
  const { can, isHouseKeeper } = usePermissions();
  const { formatDateTime } = useHotelDateTime();

  /*
   * Permission rules
   *
   * Housekeepers:
   * - Can manage notes
   * - Can update housekeeping status
   * - Cannot manually assign rooms from this dialog
   *
   * Other users:
   * - Need editHouseKeeping permission
   */
  const canEditHousekeeping = can("editHouseKeeping");

  const allowAssign = canAssign && !isHouseKeeper && canEditHousekeeping;

  const allowUpdateStatus = canUpdateStatus && (isHouseKeeper || canEditHousekeeping);

  const allowManageNote = canAddNote && (isHouseKeeper || canEditHousekeeping);

  const {
    saveNoteLoading,
    saveNoteError,

    deleteNoteLoading,
    deleteNoteError,
  } = STORE((state) => state.housekeeping);

  const [noteText, setNoteText] = useState("");

  const [isEditingNote, setIsEditingNote] = useState(false);

  const resourceState = getResourceStateConfig(room?.resourceState);

  const occupancyState = getOccupancyStateConfig(room?.occupancyState);

  const roomNote = getRoomNote(room);

  const noteActionLoading = saveNoteLoading || deleteNoteLoading;

  const noteError = saveNoteError || deleteNoteError;

  useEffect(() => {
    if (!open) return;

    setNoteText("");
    setIsEditingNote(false);

    DISPATCH(resetSaveNoteState());
    DISPATCH(resetDeleteNoteState());
  }, [open, room, DISPATCH]);

  const handleClose = () => {
    if (noteActionLoading) {
      return;
    }

    DISPATCH(resetSaveNoteState());
    DISPATCH(resetDeleteNoteState());

    onClose?.();
  };

  const handleStartEdit = () => {
    if (!roomNote || noteActionLoading || !allowManageNote) {
      return;
    }

    DISPATCH(resetSaveNoteState());
    DISPATCH(resetDeleteNoteState());

    setNoteText(getNoteText(roomNote));

    setIsEditingNote(true);
  };

  const handleCancelEdit = () => {
    if (noteActionLoading) {
      return;
    }

    DISPATCH(resetSaveNoteState());

    setNoteText("");
    setIsEditingNote(false);
  };

  const handleAddNote = async () => {
    if (
      !allowManageNote ||
      !room?.id ||
      !noteText.trim() ||
      noteActionLoading ||
      room?.version === undefined ||
      room?.version === null
    ) {
      return;
    }

    DISPATCH(resetSaveNoteState());

    const success = await onAddNote?.({
      roomId: room.id,
      message: noteText.trim(),
      version: room.version,
    });

    if (success !== false) {
      setNoteText("");
    }
  };

  const handleUpdateNote = async () => {
    if (
      !allowManageNote ||
      !room?.id ||
      !roomNote ||
      !noteText.trim() ||
      noteActionLoading ||
      room?.version === undefined ||
      room?.version === null
    ) {
      return;
    }

    DISPATCH(resetSaveNoteState());

    const handler = onUpdateNote || onAddNote;

    const success = await handler?.({
      roomId: room.id,
      message: noteText.trim(),
      version: room.version,
    });

    if (success !== false) {
      setNoteText("");
      setIsEditingNote(false);
    }
  };

  const handleDeleteNote = async () => {
    if (
      !allowManageNote ||
      !room?.id ||
      !roomNote ||
      noteActionLoading ||
      room?.version === undefined ||
      room?.version === null
    ) {
      return;
    }

    DISPATCH(resetDeleteNoteState());

    const success = await onDeleteNote?.({
      roomId: room.id,
      version: room.version,
    });

    if (success !== false) {
      setNoteText("");
      setIsEditingNote(false);
    }
  };

  const guestName = getGuestName(room);

  const guestFlag = getGuestFlag(room);

  const guestFlagType = getFlagType(guestFlag);

  const guestFlagReason = getFlagReason(guestFlag);

  const guestFlagConfig = FLAG_CONFIG[guestFlagType];

  const assignedStaff =
    room?.assignedStaff?.name ||
    room?.assignedStaff?.fullName ||
    room?.assignedStaff?.email ||
    "Unassigned";

  const hasValidUpdatedAt = room?.updatedAt && dayjs(room.updatedAt).isValid();

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle>
        <VuiBox
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
          flexWrap="wrap"
        >
          <VuiBox display="flex" alignItems="center" gap={1.2}>
            <VuiBox
              width={40}
              height={40}
              borderRadius="12px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              sx={{
                background: rgba(primary.main, 0.1),
                border: `1px solid ${rgba(primary.main, 0.2)}`,
              }}
            >
              <FaBed size={16} color={primary.main} />
            </VuiBox>

            <VuiBox>
              <VuiTypography variant="lg">
                {room?.roomName || room?.roomNumber || "Room Details"}
              </VuiTypography>
            </VuiBox>
          </VuiBox>

          <VuiBox display="flex" alignItems="center" gap={1.5}>
            <Chip
              size="small"
              label={resourceState.label}
              sx={{
                color: resourceState.color,
                background: rgba(resourceState.color, 0.12),
                border: `1px solid ${rgba(resourceState.color, 0.25)}`,
              }}
            />
          </VuiBox>
        </VuiBox>

        {room?.updatedAt && (
          <VuiTooltip
            title={hasValidUpdatedAt ? formatDateTime(room.updatedAt) : "Update time unavailable"}
          >
            <VuiBox display="flex" alignItems="center" gap={0.7} mt={1.2} width="fit-content">
              <FaClock size={10} color={primary.main} />

              <VuiTypography
                variant="caption"
                sx={{
                  opacity: 0.6,
                }}
              >
                Last Updated: {hasValidUpdatedAt ? formatDateTime(room.updatedAt) : "—"}
              </VuiTypography>
            </VuiBox>
          </VuiTooltip>
        )}
      </DialogTitle>

      <DialogContent>
        <VuiBox
          display="grid"
          gridTemplateColumns={{
            xs: "1fr",
            md: "1fr 1fr",
          }}
          gap={2}
        >
          {/* Guest Overview */}
          <VuiBox
            p={2}
            borderRadius="14px"
            sx={{
              border: `1px solid ${rgba(dark.main, 0.25)}`,
              background: "rgba(255,255,255,0.015)",
            }}
          >
            <VuiTypography
              variant="button"
              fontWeight="bold"
              sx={{
                display: "block",
                mb: 1.5,
              }}
            >
              Guest Overview
            </VuiTypography>

            <VuiBox
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={1.5}
              mb={guestFlagConfig && guestFlagReason ? 1.5 : 0}
            >
              <VuiBox display="flex" alignItems="center" gap={1} minWidth={0}>
                <VuiBox
                  width={36}
                  height={36}
                  borderRadius="10px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                  sx={{
                    color: primary.main,
                    background: rgba(primary.main, 0.12),
                    border: `1px solid ${rgba(primary.main, 0.2)}`,
                  }}
                >
                  <FaUser size={12} />
                </VuiBox>

                <VuiBox minWidth={0}>
                  <VuiTypography
                    variant="caption"
                    sx={{
                      display: "block",
                      fontSize: "10px",
                      opacity: 0.55,
                      mb: 0.2,
                    }}
                  >
                    Guest Name
                  </VuiTypography>

                  <VuiTypography
                    variant="caption"
                    fontWeight="medium"
                    sx={{
                      display: "block",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {guestName || "No active guest"}
                  </VuiTypography>
                </VuiBox>
              </VuiBox>

              <GuestFlagChip flag={guestFlag} maxWidth={160} />
            </VuiBox>

            {guestFlagConfig && guestFlagReason && (
              <VuiBox
                p={1.2}
                borderRadius="10px"
                sx={{
                  background: rgba(guestFlagConfig.color, 0.06),
                  border: `1px solid ${rgba(guestFlagConfig.color, 0.18)}`,
                }}
              >
                <VuiTypography
                  variant="caption"
                  sx={{
                    display: "block",
                    mb: 0.4,
                    fontSize: "10px",
                    color: guestFlagConfig.color,
                    fontWeight: 600,
                  }}
                >
                  {guestFlagConfig.label} Details
                </VuiTypography>

                <VuiTypography
                  variant="caption"
                  sx={{
                    display: "block",
                    lineHeight: 1.5,
                  }}
                >
                  {guestFlagReason}
                </VuiTypography>
              </VuiBox>
            )}
          </VuiBox>

          {/* Housekeeping Note */}
          <VuiBox
            p={2}
            borderRadius="14px"
            display="flex"
            flexDirection="column"
            sx={{
              border: `1px solid ${rgba(dark.main, 0.25)}`,
              background: "rgba(255,255,255,0.015)",
            }}
          >
            <VuiBox
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={1}
              mb={1.5}
            >
              <VuiBox display="flex" alignItems="center" gap={1}>
                <FaStickyNote size={12} color={primary.main} />

                <VuiTypography variant="button" fontWeight="bold">
                  Housekeeping Note
                </VuiTypography>
              </VuiBox>

              {roomNote && !isEditingNote && allowManageNote && (
                <VuiBox display="flex" alignItems="center" gap={0.5}>
                  <VuiTooltip title="Edit Note">
                    <VuiButton
                      size="small"
                      variant="outlined"
                      color="primary"
                      disabled={noteActionLoading}
                      sx={{
                        minWidth: "34px",
                        width: "34px",
                        p: 0,
                      }}
                      onClick={handleStartEdit}
                    >
                      <FaEdit size={10} />
                    </VuiButton>
                  </VuiTooltip>

                  {onDeleteNote && (
                    <VuiTooltip title="Delete Note">
                      <VuiButton
                        size="small"
                        variant="outlined"
                        color="error"
                        sx={{
                          minWidth: "34px",
                          width: "34px",
                          p: 0,
                        }}
                        disabled={noteActionLoading}
                        onClick={handleDeleteNote}
                      >
                        <FaTrash size={9} />
                      </VuiButton>
                    </VuiTooltip>
                  )}
                </VuiBox>
              )}
            </VuiBox>

            {roomNote && !isEditingNote ? (
              <VuiBox
                p={1.5}
                borderRadius="10px"
                sx={{
                  border: `1px solid ${rgba(dark.main, 0.25)}`,
                  background: rgba(light.main, 0.025),
                }}
              >
                <VuiTypography
                  variant="caption"
                  sx={{
                    display: "block",
                    whiteSpace: "pre-wrap",
                    lineHeight: 1.5,
                  }}
                >
                  {getNoteText(roomNote)}
                </VuiTypography>

                <VuiBox
                  mt={1}
                  pt={1}
                  sx={{
                    borderTop: "1px solid rgba(255,255,255,0.06)",
                  }}
                >
                  <VuiTypography
                    variant="caption"
                    sx={{
                      display: "block",
                      fontSize: "10px",
                      opacity: 0.55,
                    }}
                  >
                    {roomNote?.lastActorName ||
                      roomNote?.updatedBy ||
                      roomNote?.createdBy ||
                      "Unknown user"}

                    {roomNote?.updatedAt && dayjs(roomNote.updatedAt).isValid()
                      ? ` · Updated ${dayjs(roomNote.updatedAt).format("MM/DD/YYYY hh:mm A")}`
                      : roomNote?.createdAt && dayjs(roomNote.createdAt).isValid()
                        ? ` · ${dayjs(roomNote.createdAt).format("MM/DD/YYYY hh:mm A")}`
                        : ""}
                  </VuiTypography>
                </VuiBox>
              </VuiBox>
            ) : (
              allowManageNote && (
                <>
                  <VuiInput
                    gradient
                    multiline
                    rows={4}
                    value={noteText}
                    disabled={noteActionLoading}
                    onChange={(event) => setNoteText(event.target.value)}
                    placeholder={
                      isEditingNote ? "Edit housekeeping note..." : "Add a housekeeping note..."
                    }
                  />

                  <VuiBox display="flex" justifyContent="flex-end" gap={1} mt={1}>
                    {isEditingNote && (
                      <VuiButton
                        size="small"
                        variant="outlined"
                        color="primary"
                        disabled={noteActionLoading}
                        onClick={handleCancelEdit}
                      >
                        <FaTimes
                          size={9}
                          style={{
                            marginRight: 5,
                          }}
                        />
                        Cancel
                      </VuiButton>
                    )}

                    <VuiButton
                      size="small"
                      variant="contained"
                      color="primary"
                      disabled={!noteText.trim() || noteActionLoading}
                      onClick={isEditingNote ? handleUpdateNote : handleAddNote}
                    >
                      {isEditingNote ? (
                        <FaSave
                          size={9}
                          style={{
                            marginRight: 5,
                          }}
                        />
                      ) : (
                        <FaPlus
                          size={9}
                          style={{
                            marginRight: 5,
                          }}
                        />
                      )}

                      {saveNoteLoading ? "Saving..." : isEditingNote ? "Save Note" : "Add Note"}
                    </VuiButton>
                  </VuiBox>
                </>
              )
            )}

            {!roomNote && !allowManageNote && (
              <VuiTypography
                variant="caption"
                sx={{
                  opacity: 0.55,
                }}
              >
                No housekeeping note
              </VuiTypography>
            )}

            {noteError && (
              <VuiBox
                display="flex"
                alignItems="center"
                gap={1}
                p={1.2}
                mt={2}
                borderRadius="md"
                sx={{
                  border: "1px solid rgba(239,68,68,0.4)",
                  background: "rgba(239,68,68,0.08)",
                }}
              >
                <FaExclamationCircle size={14} color="#ef4444" />

                <VuiTypography variant="caption" color="error">
                  {typeof noteError === "string"
                    ? noteError
                    : noteError?.message || "Failed to update housekeeping note"}
                </VuiTypography>
              </VuiBox>
            )}
          </VuiBox>
        </VuiBox>

        {/* Room Overview */}
        <VuiBox
          mt={2}
          p={2}
          borderRadius="14px"
          sx={{
            border: `1px solid ${rgba(dark.main, 0.25)}`,
            background: "rgba(255,255,255,0.015)",
          }}
        >
          <VuiTypography
            variant="button"
            fontWeight="bold"
            sx={{
              display: "block",
              mb: 1.5,
            }}
          >
            Room Overview
          </VuiTypography>

          <VuiBox
            display="grid"
            gridTemplateColumns={{
              xs: "1fr",
              sm: "1fr 1fr",
              md: "1fr 1fr 1fr",
            }}
            gap={1.5}
          >
            {/* Assigned Staff */}
            <VuiBox
              display="flex"
              alignItems="center"
              gap={1}
              p={1.2}
              borderRadius="10px"
              sx={{
                background: "rgba(255,255,255,0.025)",
                border: "1px solid rgba(37, 37, 37, 0.15)",
              }}
            >
              <VuiBox
                width={32}
                height={32}
                borderRadius="9px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
                sx={{
                  background: rgba(primary.main, 0.1),
                }}
              >
                <FaUser size={11} color={primary.main} />
              </VuiBox>

              <VuiBox minWidth={0}>
                <VuiTypography
                  variant="caption"
                  sx={{
                    display: "block",
                    fontSize: "10px",
                    opacity: 0.55,
                  }}
                >
                  Assigned Staff
                </VuiTypography>

                <VuiTypography
                  variant="caption"
                  sx={{
                    display: "block",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                  }}
                >
                  {assignedStaff}
                </VuiTypography>
              </VuiBox>
            </VuiBox>

            {/* Arrival */}
            <VuiBox
              display="flex"
              alignItems="center"
              gap={1}
              p={1.2}
              borderRadius="10px"
              sx={{
                background: "rgba(59,130,246,0.04)",
                border: "1px solid rgba(59,130,246,0.1)",
              }}
            >
              <VuiBox
                width={32}
                height={32}
                borderRadius="9px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
                sx={{
                  background: "rgba(59,130,246,0.1)",
                }}
              >
                <FaSignInAlt size={11} color={info.main} />
              </VuiBox>

              <VuiBox>
                <VuiTypography
                  variant="caption"
                  sx={{
                    display: "block",
                    fontSize: "10px",
                    opacity: 0.55,
                  }}
                >
                  Arrival
                </VuiTypography>

                <VuiTypography variant="caption">
                  {room?.arrivalDate ? formatDateTime(room.arrivalDate) : "No arrival today"}
                </VuiTypography>
              </VuiBox>
            </VuiBox>

            {/* Departure */}
            <VuiBox
              display="flex"
              alignItems="center"
              gap={1}
              p={1.2}
              borderRadius="10px"
              sx={{
                background: "rgba(245,158,11,0.04)",
                border: "1px solid rgba(245,158,11,0.1)",
              }}
            >
              <VuiBox
                width={32}
                height={32}
                borderRadius="9px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
                sx={{
                  background: "rgba(245,158,11,0.1)",
                }}
              >
                <FaSignOutAlt size={11} color={warning.main} />
              </VuiBox>

              <VuiBox>
                <VuiTypography
                  variant="caption"
                  sx={{
                    display: "block",
                    fontSize: "10px",
                    opacity: 0.55,
                  }}
                >
                  Departure
                </VuiTypography>

                <VuiTypography variant="caption">
                  {room?.departureDate ? formatDateTime(room.departureDate) : "No departure today"}
                </VuiTypography>
              </VuiBox>
            </VuiBox>
          </VuiBox>
        </VuiBox>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <VuiButton
          variant="outlined"
          color="primary"
          onClick={handleClose}
          disabled={noteActionLoading}
        >
          Close
        </VuiButton>

        {/* Only non-housekeepers with edit permission */}
        {allowAssign && (
          <VuiButton
            variant="outlined"
            color="primary"
            onClick={onAssign}
            disabled={noteActionLoading}
          >
            <FaUserPlus
              size={10}
              style={{
                marginRight: 6,
              }}
            />
            Assign
          </VuiButton>
        )}

        {/* Housekeeper OR editHouseKeeping permission */}
        {allowUpdateStatus && (
          <VuiButton
            variant="contained"
            color="primary"
            onClick={onUpdateStatus}
            disabled={noteActionLoading}
          >
            <FaBroom
              size={10}
              style={{
                marginRight: 6,
              }}
            />
            Update Status
          </VuiButton>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default RoomDetailDialog;
